import { videos } from '@/mocks/videos';
import { categories } from '@/mocks/categories';

export interface Video {
  id: string;
  title: string;
  cover: string;
  category: string;
  duration: string;
  views: number;
  publishedAt: string;
  author: string;
  description: string;
  recommendReason: string;
  tags: string[];
  featured?: boolean;
  hot?: boolean;
}

export interface Category {
  slug: string;
  name: string;
  short: string;
  icon: string;
  description: string;
  cover: string;
}

export type SortKey = 'latest' | 'views';

export const getCategories = (): Category[] => categories as Category[];

export const getCategory = (slug?: string): Category | undefined =>
  (categories as Category[]).find((item) => item.slug === slug);

export const getCategoryName = (slug: string): string =>
  (categories as Category[]).find((item) => item.slug === slug)?.name ?? '其他';

export const getVideoById = (id?: string): Video | undefined =>
  (videos as Video[]).find((item) => item.id === id);

export const getVideosByCategory = (slug: string): Video[] =>
  (videos as Video[]).filter((item) => item.category === slug);

export const getFeaturedVideos = (): Video[] =>
  (videos as Video[]).filter((item) => item.featured);

export const getLatestVideos = (limit?: number): Video[] => {
  const list = [...(videos as Video[])].sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
  );
  return typeof limit === 'number' ? list.slice(0, limit) : list;
};

export const getHotVideos = (limit?: number): Video[] => {
  const list = [...(videos as Video[])].sort((a, b) => b.views - a.views);
  return typeof limit === 'number' ? list.slice(0, limit) : list;
};

export const sortVideos = (list: Video[], key: SortKey): Video[] => {
  const sorted = [...list];
  if (key === 'views') {
    sorted.sort((a, b) => b.views - a.views);
  } else {
    sorted.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  }
  return sorted;
};

export const searchVideos = (keyword: string): Video[] => {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return (videos as Video[]).filter((item) => {
    const categoryName = getCategoryName(item.category);
    return (
      item.title.toLowerCase().includes(kw) ||
      categoryName.includes(kw) ||
      item.tags.some((tag) => tag.toLowerCase().includes(kw)) ||
      item.author.toLowerCase().includes(kw)
    );
  });
};

export const getRelatedVideos = (video: Video, limit = 4): Video[] => {
  return (videos as Video[])
    .filter((item) => item.id !== video.id && item.category === video.category)
    .sort((a, b) => b.views - a.views)
    .slice(0, limit);
};

export const formatViews = (views: number): string => {
  if (views >= 100000000) return `${(views / 100000000).toFixed(1)}亿`;
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  if (views >= 1000) return `${(views / 1000).toFixed(1)}千`;
  return `${views}`;
};

export const formatDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
};

export const formatDateCN = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.getMonth() + 1}月${date.getDate()}日`;
};